import React, { useEffect, useMemo, useRef, useState } from 'react';

const SearchIcon = () => (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6">
        <circle cx="7" cy="7" r="4.5" />
        <path d="M10.5 10.5L14 14" />
    </svg>
);

export const SearchClear = ({ visible, onClear, disabled }) => {
    if (!visible) return null;
    return (
        <button
            type="button"
            className="pc-srch-x"
            aria-label="Очистить"
            title="Очистить"
            disabled={disabled}
            onMouseDown={(event) => event.preventDefault()}
            onClick={onClear}
        >
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6">
                <path d="M4 4l8 8M12 4l-8 8" />
            </svg>
        </button>
    );
};

export const pickerItemId = (item) => String(item?.id ?? item?._id ?? '');

export const pickerItemLabel = (item) => String(
    item?.fullname || item?.name || item?.title || item?.label || ''
).trim();

export const pickerItemMeta = (item) => String(item?.login || item?.code || '').trim();

export const pickerMatches = (item, query, getLabel = pickerItemLabel, getMeta = pickerItemMeta, getId = pickerItemId) => {
    if (!query) return true;
    const q = String(query).toLowerCase().trim();
    if (!q) return true;
    return [getLabel(item), getMeta(item), getId(item)]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(q));
};

const SearchablePicker = ({
    label,
    required,
    placeholder = 'Найти',
    options = [],
    value,
    onChange,
    disabled,
    loading,
    error,
    style,
    className,
    emptyText = 'Ничего не найдено',
    maxItems = 80,
    getId = pickerItemId,
    getLabel = pickerItemLabel,
    getMeta = pickerItemMeta,
    filterItem,
    clearOnType = false,
    inputId,
}) => {
    const wrapRef = useRef(null);
    const inputRef = useRef(null);
    const [query, setQuery] = useState('');
    const [open, setOpen] = useState(false);
    const [dirty, setDirty] = useState(false);
    const [hi, setHi] = useState(0);

    const selected = useMemo(
        () => (options || []).find((item) => String(getId(item)) === String(value || '')) || null,
        [options, value, getId]
    );

    useEffect(() => {
        if (!dirty) setQuery(selected ? getLabel(selected) : '');
    }, [selected, dirty, getLabel]);

    const matches = useMemo(() => {
        const list = options || [];
        const needle = dirty ? query : '';
        const next = list.filter((item) => (
            filterItem
                ? filterItem(item, needle)
                : pickerMatches(item, needle, getLabel, getMeta, getId)
        ));
        return next.slice(0, maxItems);
    }, [options, dirty, query, filterItem, getLabel, getMeta, getId, maxItems]);

    useEffect(() => {
        setHi(0);
    }, [query, open]);

    useEffect(() => {
        if (!open) return undefined;
        const onDoc = (event) => {
            if (wrapRef.current && !wrapRef.current.contains(event.target)) {
                setOpen(false);
                setDirty(false);
                setQuery(selected ? getLabel(selected) : '');
            }
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, [open, selected, getLabel]);

    const pick = (item) => {
        if (!item) return;
        onChange(item);
        setQuery(getLabel(item));
        setDirty(false);
        setOpen(false);
        setHi(0);
    };

    const display = dirty ? query : (selected ? getLabel(selected) : query);

    const clear = () => {
        setQuery('');
        setDirty(true);
        setHi(0);
        setOpen(!disabled && !loading);
        if (value) onChange(null);
        if (inputRef.current) inputRef.current.focus();
    };

    return (
        <div className={`pc-pick${className ? ` ${className}` : ''}${error ? ' is-invalid' : ''}`} style={style}>
            {label ? (
                <label className="pc-field__label" htmlFor={inputId}>
                    {label}
                    {required ? <i>*</i> : null}
                </label>
            ) : null}
            <div className="pc-pick-combo" ref={wrapRef}>
                <div className="pc-pick-srch">
                    <SearchIcon />
                    <input
                        id={inputId}
                        ref={inputRef}
                        className="pc-pick-inp"
                        type="text"
                        autoComplete="off"
                        disabled={disabled || loading}
                        placeholder={loading ? 'Загрузка…' : placeholder}
                        value={display}
                        onFocus={() => {
                            if (!disabled && !loading) setOpen(true);
                        }}
                        onChange={(event) => {
                            const next = event.target.value;
                            setQuery(next);
                            setDirty(true);
                            setOpen(true);
                            if (clearOnType && value) onChange(null);
                            if (!next && !clearOnType) onChange(null);
                        }}
                        onKeyDown={(event) => {
                            if (event.key === 'ArrowDown') {
                                event.preventDefault();
                                setOpen(true);
                                setHi((index) => Math.min(index + 1, Math.max(0, matches.length - 1)));
                            } else if (event.key === 'ArrowUp') {
                                event.preventDefault();
                                setHi((index) => Math.max(0, index - 1));
                            } else if (event.key === 'Enter') {
                                event.preventDefault();
                                pick(matches[hi] || matches[0]);
                            } else if (event.key === 'Escape' && open) {
                                event.preventDefault();
                                event.stopPropagation();
                                setOpen(false);
                                setDirty(false);
                                setQuery(selected ? getLabel(selected) : '');
                            }
                        }}
                    />
                    <SearchClear
                        visible={Boolean(display) && !disabled && !loading}
                        onClear={clear}
                    />
                </div>
                {open && !disabled && !loading ? (
                    <div className="pc-pick-drop" role="listbox">
                        {matches.length ? matches.map((item, index) => {
                            const id = getId(item);
                            const on = index === hi || String(id) === String(value || '');
                            const meta = getMeta(item);
                            return (
                                <button
                                    type="button"
                                    key={id || index}
                                    role="option"
                                    aria-selected={on}
                                    className={on ? 'is-on' : undefined}
                                    onMouseEnter={() => setHi(index)}
                                    onMouseDown={(event) => event.preventDefault()}
                                    onClick={() => pick(item)}
                                >
                                    <span>{getLabel(item) || '—'}</span>
                                    {meta ? <i>{meta}</i> : null}
                                </button>
                            );
                        }) : (
                            <div className="pc-pick-empty">{emptyText}</div>
                        )}
                    </div>
                ) : null}
            </div>
            {error ? <em className="pc-field__hint is-err">{error}</em> : null}
        </div>
    );
};

export default SearchablePicker;
